import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MessageCircleHeart } from 'lucide-react'
import { ZODIAC_SIGNS, getSunSign } from '@yadviga/shared'
import { api } from '../api/client'
import { useSession } from '../store/user.store'
import { TarotCardImage } from '../components/tarot/TarotCardImage'
import { LoadingDots } from '../components/ui/LoadingDots'
import { ErrorState } from '../components/ui/ErrorState'
import { MarkdownText } from '../components/ui/Markdown'
import { ShareButton } from '../components/ui/ShareButton'
import { SectionHeader } from '../components/ui/SectionHeader'

export function HomeRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)

  const dailyQuery = useQuery({
    queryKey: ['tarot', 'daily'],
    queryFn: () => api.dailyCard(),
    staleTime: 1000 * 60 * 60,
  })

  const horoscopeQuery = useQuery({
    queryKey: ['horoscope', 'today'],
    queryFn: () => api.horoscopeToday(),
    enabled: Boolean(user?.birthDate),
    staleTime: 1000 * 60 * 60,
  })

  const lunarQuery = useQuery({
    queryKey: ['daily', 'lunar'],
    queryFn: () => api.lunarToday(),
    staleTime: 1000 * 60 * 60,
  })

  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const userSign = user?.birthDate
    ? (() => {
        const [_, m, d] = user.birthDate.split('-').map(Number)
        return getSunSign(m!, d!)
      })()
    : null
  const userSignLabel = userSign
    ? ZODIAC_SIGNS.find((s) => s.id === userSign)?.[lang === 'en' ? 'nameEn' : 'nameRu']
    : null

  return (
    <div className="flex flex-col gap-7 pb-6">
      <header className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-widest text-sage-500">
          {todayLabel(user?.language ?? 'ru')}
        </p>
        <h1 className="font-serif text-3xl text-sage-800">
          {user?.name ? t('app.greeting', { name: user.name }) : 'Привет'}
        </h1>
      </header>

      {/* Deep-link tile grid. Each tile points to a specific feature, not a
          section index — fewer clicks to content. Bottom tabs handle the
          coarse "jump between sections" need. 3 columns × 2 rows = 6 tiles. */}
      <section>
        <SectionHeader title={t('home.explore')} />
        <div className="grid grid-cols-3 gap-2">
          <QuickAction to="/luna"            iconSrc="/icons/forecast.png"    title={t('home.tile_luna_today')}  accent="twilight" />
          <QuickAction to="#today-card"      iconSrc="/icons/tarot_daily.png" title={t('home.tile_daily_card')}  accent="copper"   scroll />
          <QuickAction to="/numbers/day"     iconSrc="/icons/numbers.png"     title={t('home.tile_day_number')}  accent="sage" />
          <QuickAction to="/astro/natal"     iconSrc="/icons/natal.png"       title={t('home.tile_natal')}       accent="sage" />
          <QuickAction to="/compat"          iconSrc="/icons/compat.png"      title={t('home.tile_compat')}      accent="blush" />
          <QuickAction to="/luna/month"      iconSrc="/icons/luna_month.png"  title={t('home.tile_luna_month')}  accent="twilight" />
        </div>
      </section>

      {/* Chat tile promoted right under the grid — users couldn't find it
          when it lived below all the daily-content sections. */}
      <Link
        to="/chat"
        className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#fbeadd] to-[#f5d9c0] p-5 ring-1 ring-[#e6c4a4]/60 transition active:scale-[0.99]"
      >
        <MessageCircleHeart className="h-9 w-9 shrink-0 text-copper" strokeWidth={1.5} aria-hidden />
        <div className="flex min-w-0 flex-col">
          <span className="font-serif text-lg text-sage-800">{t('home.chat_cta_title')}</span>
          <span className="text-xs text-sage-600">{t('home.chat_cta_sub')}</span>
        </div>
      </Link>

      {lunarQuery.data && (
        <section>
          <SectionHeader
            eyebrow={t('home.lunar_eyebrow')}
            title={t(`home.moon_phase_${lunarQuery.data.phase}`)}
          />
          <div className="rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
            <p className="text-sage-800 leading-relaxed">
              {lunarQuery.data.energyHint[lang]}
            </p>
            <p className="mt-2 text-xs text-sage-500">
              {t('home.lunar_illumination', { percent: Math.round(lunarQuery.data.illumination * 100) })}
            </p>
          </div>
        </section>
      )}

      <section id="today-card" className="scroll-mt-4">
        <SectionHeader title={t('home.today_card')} />
        {dailyQuery.isLoading && <LoadingDots pose="tarot" />}
        {dailyQuery.isError && <ErrorState onRetry={() => dailyQuery.refetch()} />}
        {dailyQuery.data && (
          <div className="flex flex-col items-center gap-4 rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
            <TarotCardImage
              cardId={dailyQuery.data.cardId}
              reversed={dailyQuery.data.reversed}
              size="hero"
            />
            <div className="text-center text-sage-800">
              <MarkdownText>{dailyQuery.data.interpretation}</MarkdownText>
            </div>
            <ShareButton
              variant="ghost"
              size="sm"
              text={t('home.today_card')}
            />
          </div>
        )}
      </section>

      {horoscopeQuery.data && userSignLabel && (
        <section>
          <SectionHeader
            eyebrow={userSignLabel.toUpperCase()}
            title={t('forecast.horoscope_title')}
          />
          <div className="rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
            <MarkdownText>{horoscopeQuery.data.text}</MarkdownText>
          </div>
        </section>
      )}

    </div>
  )
}

const ACCENT_CLASSES: Record<'sage' | 'copper' | 'twilight' | 'blush', string> = {
  sage: 'bg-sage-50 ring-sage-200/60 hover:bg-sage-100',
  copper: 'bg-[#fbeadd] ring-[#e6c4a4]/60 hover:bg-[#f5d9c0]',
  twilight: 'bg-[#e3e6f0] ring-[#a8b3d6]/60 hover:bg-[#d3d8e7]',
  blush: 'bg-[#fbe5e6] ring-[#f0bfb7]/60 hover:bg-[#f7d4d6]',
}

function QuickAction({
  to,
  iconSrc,
  title,
  accent,
  scroll,
}: {
  to: string
  iconSrc: string
  title: string
  accent: keyof typeof ACCENT_CLASSES
  /** When true, treat `to` as an in-page anchor and scrollIntoView instead of routing. */
  scroll?: boolean
}) {
  const className = `flex flex-col items-center gap-1.5 rounded-2xl px-2 py-3 text-center ring-1 transition active:scale-[0.98] ${ACCENT_CLASSES[accent]}`
  const body = (
    <>
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className="h-9 w-9 shrink-0 select-none"
        draggable={false}
      />
      <span className="font-serif text-[12px] leading-tight text-sage-800 break-words">{title}</span>
    </>
  )
  if (scroll) {
    return (
      <a
        href={to}
        onClick={(e) => {
          e.preventDefault()
          const id = to.replace(/^#/, '')
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }}
        className={className}
      >
        {body}
      </a>
    )
  }
  return <Link to={to} className={className}>{body}</Link>
}

function todayLabel(language: string): string {
  const fmt = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  return fmt.format(new Date())
}
